import { Injectable } from '@angular/core';
import { BehaviorSubject, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { CommonFunctionsService, DialogType } from './common-functions.service';
import { NotificationType, SilentActions, } from './definitions.service';
import firebase from 'firebase/app';
import * as i0 from "@angular/core";
import * as i1 from "@angular/fire/messaging";
import * as i2 from "./common-functions.service";
import * as i3 from "./log.service";
import * as i4 from "./auth.service";
import * as i5 from "./data-model.service";
import * as i6 from "@angular/router";
var MessagingService = /** @class */ (function () {
    function MessagingService(angularFireMessaging, commonFunctions, log, authenticationService, dataModel, router) {
        this.angularFireMessaging = angularFireMessaging;
        this.commonFunctions = commonFunctions;
        this.log = log;
        this.authenticationService = authenticationService;
        this.dataModel = dataModel;
        this.router = router;
        this.currentMessage = new BehaviorSubject(null);
        this.isRequestingToken = new BehaviorSubject(false);
        this.unreadCount$ = of(0);
        // Tracks FCM message IDs that have already been saved to Firestore inbox.
        // Prevents duplicate entries when a background message is saved via the
        // BACKGROUND_MESSAGE_RECEIVED broadcast AND the user subsequently clicks the
        // OS notification (triggering a second NOTIFICATION_CLICK save attempt).
        this.savedMessageIds = new Set();
    }
    /** Call once after login to wire up the unread-count observable. */
    MessagingService.prototype.initInbox = function (userId) {
        this.unreadCount$ = this.dataModel
            .subscribeToInboxNotifications(userId)
            .pipe(map(function (notifications) { return notifications.filter(function (n) { return !n.read; }).length; }));
    };
    //Starts asking if user wants to receive push notification
    MessagingService.prototype.requestPermission = function (force) {
        if (force === void 0) { force = false; }
        if (this.authenticationService.isLoggedIn) {
            this.requestNotificationPermission(this.authenticationService.userDetails.uid, force);
        }
        else {
            this.commonFunctions.showErrorToast('User nicht eingeloggt');
        }
    };
    MessagingService.prototype.receiveMessage = function () {
        var _this = this;
        // Foreground messages (app is open)
        this.angularFireMessaging.messages.subscribe(function (payload) {
            console.log('Message received. ', payload);
            _this.currentMessage.next(payload);
            _this.handleIncomingPayload(payload, false);
        });
        // Service worker messages: only background message receipts remain here.
        // Notification clicks are handled via URL query params in NotificationInboxComponent.
        if (navigator.serviceWorker) {
            navigator.serviceWorker.addEventListener('message', function (event) {
                var msg = event.data;
                if (!msg || !msg.payload)
                    return;
                if (msg.type === 'BACKGROUND_MESSAGE_RECEIVED') {
                    // App was open in a background tab when the push arrived.
                    // Save silently to Firestore so the inbox and badge update immediately
                    // without any dialog or navigation interrupting the user.
                    console.log('Background message received while app was open. ', msg.payload);
                    var payload = msg.payload;
                    _this.currentMessage.next(payload);
                    _this.handleIncomingPayload(payload, false, false /* showDialog */);
                }
            });
        }
    };
    /**
     * Handles a received FCM payload: saves to inbox and optionally navigates.
     * @param navigateToInbox When true, navigate to /notifications instead of showing a dialog.
     * @param showDialog When false, suppress the in-app dialog (used for background-tab saves).
     */
    MessagingService.prototype.handleIncomingPayload = function (payload, navigateToInbox, showDialog) {
        var _this = this;
        var _a, _b;
        if (showDialog === void 0) { showDialog = true; }
        var data = (payload === null || payload === void 0 ? void 0 : payload.data) || {};
        var messageType = parseInt(data.messageType, 10) || 0;
        // Silent actions never go to the inbox
        if (messageType === NotificationType.SilentAction) {
            this.handleSilentActions(parseInt(data.silentActionId, 10));
            return;
        }
        // Save to Firestore inbox — skip if we already saved this message ID
        // (prevents duplicates when BACKGROUND_MESSAGE_RECEIVED and the subsequent
        // URL-param path both try to save the same push).
        var userId = this.authenticationService.userId;
        var messageId = payload.fcmMessageId;
        if (userId && !(messageId && this.savedMessageIds.has(messageId))) {
            var entry = {
                fcmMessageId: messageId || undefined,
                title: ((_a = payload.notification) === null || _a === void 0 ? void 0 : _a.title) || data.messageTitle || 'Residenz App',
                body: ((_b = payload.notification) === null || _b === void 0 ? void 0 : _b.body) || data.messageBody || '',
                messageType: messageType,
                messageTitle: data.messageTitle || '',
                messageBody: data.messageBody || '',
                senderUid: data.senderUid || '',
                senderName: data.senderName || '',
                route: data.route || '',
                timestamp: firebase.firestore.Timestamp.fromDate(new Date()),
                read: false,
            };
            this.dataModel.saveInboxNotification(userId, entry);
            if (messageId) {
                this.savedMessageIds.add(messageId);
                // Clean up after 10 minutes to avoid unbounded set growth
                setTimeout(function () { return _this.savedMessageIds.delete(messageId); }, 10 * 60 * 1000);
            }
        }
        if (navigateToInbox) {
            this.router.navigate(['/notifications']);
            return;
        }
        if (showDialog) {
            this.displayNotification(messageType, data);
        }
    };
    //Shows a push notification dialog
    MessagingService.prototype.displayNotification = function (messageType, data) {
        var title = data.messageTitle || '';
        var body = data.messageBody || '';
        if (messageType === NotificationType.Standard) {
            this.commonFunctions.openDialog(title, body);
        }
        else if (messageType === NotificationType.MessageFromAdmin) {
            this.commonFunctions.openDialog('Nachricht vom Admin', body);
        }
        else if (messageType === NotificationType.GeneralReminder) {
            this.commonFunctions.openDialog('Erinnerung', body);
        }
        else if (messageType === NotificationType.TestMessage) {
            this.commonFunctions.openDialog('\uD83E\uDDEA Test Notification', body);
        }
        else {
            this.commonFunctions.openDialog(title || 'Residenz App', body);
        }
        if (data.route) {
            this.router.navigate(['/' + data.route]);
        }
    };
    /**
     * Called by NotificationInboxComponent when the app opens via a notification
     * click URL (`/notifications?n=<encoded>`). Reconstructs the FCM payload from
     * the compact URL data and saves it to the Firestore inbox — unless already
     * saved in this session via BACKGROUND_MESSAGE_RECEIVED (deduplication via
     * savedMessageIds).
     */
    MessagingService.prototype.processNotificationFromUrl = function (compact) {
        var payload = {
            fcmMessageId: compact.id,
            from: '',
            priority: '',
            notification: { title: compact.t, body: compact.b },
            data: {
                messageType: compact.mt,
                messageTitle: compact.mtl,
                messageBody: compact.mbl,
                senderUid: compact.uid,
                senderName: compact.name,
                route: compact.rt,
                silentActionId: compact.said,
            },
        };
        this.handleIncomingPayload(payload, false, false);
    };
    //Handles silent actions without any user notification
    MessagingService.prototype.handleSilentActions = function (silentActionId) {
        if (silentActionId === SilentActions.SendLogFilesToDev) {
            this.log
                .sendLogsToDev(this.authenticationService.userId)
                .then(function () { return console.log('Logs erfolgreich versandt'); }, function (rejectMsg) { return console.log('Logs nicht versandt: ' + rejectMsg); });
        }
        if (silentActionId === SilentActions.LogOut) {
            this.authenticationService.logout();
        }
        if (silentActionId === SilentActions.ClearLogFiles) {
            this.log.clearLogs();
        }
    };
    /**
     * Requests permission to retrieve web notifications
     * Source: https://github.com/angular/angularfire/blob/master/docs/messaging/messaging.md
     */
    MessagingService.prototype.requestNotificationPermission = function (userId, force) {
        var _this = this;
        if (force === void 0) { force = false; }
        //already requested?
        var alreadyRequested = localStorage.getItem('notiTokenRequested');
        if (alreadyRequested == null || force) {
            this.commonFunctions
                .openDialog('Benachrichtigungen', 'Möchtest du Push-Benachrichtigungen erhalten?', DialogType.INFO, 'Nein', 'Ja')
                .afterClosed()
                .subscribe(function (result) {
                localStorage.setItem('notiTokenRequested', 'true');
                if (result.result == 1) {
                    _this.isRequestingToken.next(true);
                    _this.angularFireMessaging.requestToken.subscribe(function (token) {
                        _this.isRequestingToken.next(false);
                        _this.log.debug('Permission for receiving notifications granted. Sending token to server', token, CommonFunctionsService.name);
                        _this.commonFunctions.saveNotiTokenInUserCollection(userId, token);
                    }, function (error) {
                        _this.isRequestingToken.next(false);
                        _this.commonFunctions.showErrorToast('Registrierung nicht möglich');
                        _this.log.error('Request for receiving notification token failed', error, CommonFunctionsService.name);
                    });
                }
            });
        }
    };
    MessagingService.ɵfac = function MessagingService_Factory(t) { return new (t || MessagingService)(i0.ɵɵinject(i1.AngularFireMessaging), i0.ɵɵinject(i2.CommonFunctionsService), i0.ɵɵinject(i3.LogService), i0.ɵɵinject(i4.AuthService), i0.ɵɵinject(i5.DataModelService), i0.ɵɵinject(i6.Router)); };
    MessagingService.ɵprov = i0.ɵɵdefineInjectable({ token: MessagingService, factory: MessagingService.ɵfac, providedIn: 'root' });
    return MessagingService;
}());
export { MessagingService };
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MessagingService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: i1.AngularFireMessaging }, { type: i2.CommonFunctionsService }, { type: i3.LogService }, { type: i4.AuthService }, { type: i5.DataModelService }, { type: i6.Router }]; }, null); })();
//# sourceMappingURL=messaging.service.js.map